import React from 'react';
import ConditionalLink from '@plone/volto/components/manage/ConditionalLink/ConditionalLink';
import Image from '@plone/volto/components/theme/Image/Image';
import cx from 'classnames';
import { ContentImage } from 'govbr.frontend.policy/components';
import moment from 'moment';
import { useIntl } from 'react-intl';

import styles from './Listing.module.less';

const hasValidLink = (item) =>
  Boolean(item?.['@id'] || item?.url || item?.href || item?.contentUrl);

export const Subtitle = ({ subtitle }) =>
  subtitle ? (
    <div
      className={cx(styles.subtitle, 'text-up-01 text-gray-70 text-semi-bold')}
    >
      {subtitle}
    </div>
  ) : null;

export const Title = ({ item, isEditMode }) => {
  const { title, review_state } = item;
  const canLink = hasValidLink(item);

  if (!title) return null;

  const classesTitle = cx(
    styles.title,
    'text-up-02 text-medium text-interactive m-0 mb-2 mb-sm-1',
    {
      [`review-state-${review_state}`]: review_state,
    },
  );

  return (
    <h3 className={classesTitle}>
      <ConditionalLink
        item={item}
        condition={!isEditMode && canLink}
        className={classesTitle}
        aria-label={`Leia mais sobre ${title}`}
      >
        {title}
      </ConditionalLink>
    </h3>
  );
};

export const Thumbnail = ({ item, isEditMode, alt }) =>
  item.image_field || item.image_url || item.image ? (
    <div className={styles.thumbWrapper}>
      <ConditionalLink
        item={item}
        condition={!isEditMode && hasValidLink(item)}
      >
        <ContentImage
          item={item}
          alt={alt}
          size="mini"
          className={styles.thumb}
        />
      </ConditionalLink>
    </div>
  ) : null;

export const ThumbnailWithoutSize = ({ item, isEditMode, alt, className }) =>
  item.image_field ? (
    <div className={cx(styles.thumbWrapper, className)}>
      <ConditionalLink item={item} condition={!isEditMode}>
        {/* WIDTH < 200 FOR MINI */}
        <Image item={item} alt={alt} className={styles.thumb} width={199} />
      </ConditionalLink>
    </div>
  ) : null;

export const DatePublished = ({ item }) => {
  const { EffectiveDate } = item;
  const publishDate = EffectiveDate;

  const intl = useIntl();
  const normalizedLocale = intl?.locale
    ? intl.locale.toLowerCase().replace(/_/g, '-')
    : null;
  const getMomentInstance = (date) =>
    normalizedLocale ? moment(date).locale(normalizedLocale) : moment(date);

  if (!publishDate || !getMomentInstance(publishDate).isValid()) return null;

  return (
    <time
      className="text-base"
      dateTime={getMomentInstance(publishDate).format('YYYY-MM-DDTHH:mm:ss')}
    >
      {getMomentInstance(publishDate).format('DD/MM/YYYY HH:mm')}
    </time>
  );
};

export const Description = ({ item, hideDatePublished = false, className }) => {
  if (!item.description && !item.EffectiveDate) return null;

  return (
    <span
      className={cx(
        styles.description,
        'm-0 mb-1 d-block text-up-01 text-gray-80',
        className,
      )}
    >
      {!hideDatePublished && <DatePublished item={item} />}
      {item.description && !hideDatePublished && ' - '}
      {item.description}
    </span>
  );
};

export const Category = ({ category }) =>
  category && (
    <div className={cx(styles.category)}>
      <span className="text-gray-70 text-medium">Categoria: </span>
      <span className="text-gray-80">{category}</span>
    </div>
  );

export const EventDateLocation = ({
  item,
  dateFormat = 'DD/MM/YYYY',
  timeFormat = 'HH:mm',
  onlyDate = false,
}) => {
  const { start, end, location, whole_day = false, open_end = false } = item;
  if (!start) return null;

  const startDate = moment(start).format(dateFormat);
  const endDate = end ? moment(end).format(dateFormat) : null;
  const startTime = moment(start).format(timeFormat);
  const endTime = end ? moment(end).format(timeFormat) : null;
  const showAllDayLabel = whole_day && !open_end;

  return (
    <div className="data-local-evento text-up-01 d-flex flex-column flex-md-row mb-0 mb-sm-2">
      <span className="data-evento mr-3x">
        <i
          className="far fa-clock mr-2 text-primary-default"
          aria-hidden="true"
        ></i>
        {whole_day ? (
          <>
            {open_end ? (
              <span className="data-evento-inicio">{startDate}</span>
            ) : endDate && startDate !== endDate ? (
              <>
                De <span className="data-evento-inicio">{startDate}</span>
                &nbsp;até&nbsp;
                <span className="data-evento-fim">{endDate}</span>
              </>
            ) : (
              <span className="data-evento-inicio">{startDate}</span>
            )}
            {showAllDayLabel && <span> (durante todo o dia)</span>}
          </>
        ) : open_end ? (
          <>
            <span className="data-evento-inicio">{startDate}</span>
            &nbsp;às&nbsp;
            <span className="data-evento-inicio-hora">{startTime}</span>
          </>
        ) : endDate && startDate !== endDate ? (
          <>
            De <span className="data-evento-inicio">{startDate}</span>
            &nbsp;às&nbsp;
            <span className="data-evento-inicio-hora">{startTime}</span>
            &nbsp;até&nbsp;
            <span className="data-evento-fim">{endDate}</span>
            &nbsp;às&nbsp;
            <span className="data-evento-fim-hora">{endTime}</span>
          </>
        ) : (
          <>
            <span className="data-evento-inicio">{startDate}</span>
            {end && (
              <>
                &nbsp;das&nbsp;
                <span className="data-evento-inicio-hora">{startTime}</span>
                &nbsp;às&nbsp;
                <span className="data-evento-fim-hora">{endTime}</span>
              </>
            )}
          </>
        )}
      </span>
      {location && !onlyDate && (
        <span className="localizacao-evento">
          <i
            className="fas fa-map-marker-alt mr-2 text-primary-default"
            aria-hidden="true"
          ></i>
          {location}
        </span>
      )}
    </div>
  );
};
